import React from 'react';
import { logGlobalError } from '../utils/globalErrorLogger';
import { clientEnvironment } from 'shared/platform/environment';
import {
  CHUNK_RELOAD_GUARD_KEY,
  currentBuildInfo,
  getSafeSessionStorage,
  isChunkLoadFailure,
  isFirestoreInternalAssertion,
  reloadForFirestoreInternalAssertion,
} from 'shared/services/appUpdate';

/**
 * App-root error boundary. React render-phase errors don't reach window.onerror,
 * so this catches them and forwards to Cloud Logging via `logGlobalError`.
 * Production fallback is intentionally minimal — a reload usually clears
 * transient render failures. Dev builds show the stack.
 *
 * Chunk-load failures are treated as a stale build rather than a defect: they
 * mean this tab is running an `index.html` whose chunks were replaced by a
 * deploy. `lazyNamed` retries and reloads before it gets here, so reaching this
 * point means recovery was already attempted and declined — the user is told to
 * reload, and the entry is logged as a WARNING so it stays out of Error
 * Reporting's defect list.
 *
 * A Firestore internal assertion failure is handled the same way, for the same
 * reason: the SDK's async queue is dead, so nothing on the page can recover
 * without a reload. One reload is attempted per session (see
 * `reloadForFirestoreInternalAssertion`); if that already happened, the user
 * gets the generic fallback. Logged as a WARNING because the defect is inside
 * `firebase/firestore`, not here.
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  isStaleBuild?: boolean;
}

/** Reasons the boundary treats as "the tab is stale/broken", not as a defect. */
type RecoverableCause = 'staleBuild' | 'firestoreAssertion' | null;

function recoverableCause(error: Error): RecoverableCause {
  if (isChunkLoadFailure(error)) return 'staleBuild';
  if (isFirestoreInternalAssertion(error)) return 'firestoreAssertion';
  return null;
}

const IS_DEV = !clientEnvironment.isProduction;

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, isStaleBuild: isChunkLoadFailure(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const cause = recoverableCause(error);
    const isStaleBuild = cause === 'staleBuild';
    const metadata: Record<string, unknown> = { componentStack: info.componentStack ?? undefined };
    if (cause === 'firestoreAssertion') {
      const reloading = reloadForFirestoreInternalAssertion();
      logGlobalError(error, {
        context: 'FirestoreInternalAssertionFailure',
        metadata: {
          ...metadata,
          recovery: reloading ? 'reloading' : 'alreadyReloadedThisSession',
        },
        severity: 'WARNING',
      });
      return;
    }
    if (isStaleBuild) {
      // `lazyNamed`/`lazyDefault` already retried and reloaded once for this
      // build before the error reached here; recording that build id and
      // whether the reload guard had already fired lets us tell "recovery
      // hasn't run yet" apart from "recovery ran and still failed" in Cloud
      // Logging, which the raw error message alone can't distinguish.
      const build = currentBuildInfo();
      metadata.buildId = build.buildId;
      metadata.gitSha = build.gitSha;
      metadata.reloadAlreadyAttempted =
        getSafeSessionStorage()?.getItem(CHUNK_RELOAD_GUARD_KEY) === build.buildId;
    }
    logGlobalError(error, {
      context: isStaleBuild ? 'StaleBuildChunkLoadFailure' : 'ReactErrorBoundary',
      metadata,
      severity: isStaleBuild ? 'WARNING' : 'ERROR',
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-slate-900">
            {this.state.isStaleBuild ? 'A new version is available' : 'Something went wrong'}
          </h1>
          <p className="mt-2 text-slate-600">
            {this.state.isStaleBuild
              ? 'This tab is running an older version of the app. Reload to get the latest one.'
              : 'The page hit an unexpected error. Reloading usually clears it.'}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Reload page
          </button>
          {IS_DEV && this.state.error ? (
            <pre className="mt-6 max-h-64 overflow-auto rounded bg-red-50 p-3 text-left text-xs text-red-900">
              {this.state.error.stack || this.state.error.message}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}
